/* eslint-disable no-nested-ternary */
import React from "react";
import { useField } from "react-final-form";
import { CheckCircle, CheckCircleOutline } from "@mui/icons-material";
import { Box, Paper, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { groupBy } from "lodash";

import { Table, TableContainer } from "./RequiredFiles.styles";

interface IProps {
  // The name of the field in the form
  fieldName: string;

  // The list of files with their titles
  fileMap: Array<{ title: string; file: string }>;
}

const RequiredFiles: React.FC<IProps> = ({ fileMap, fieldName }) => {
  const {
    input: { value },
    meta: { error, touched, submitError },
  } = useField<File[] | undefined>(fieldName);
  const hasError = (error || submitError) && touched;

  const groupedFiles = groupBy(fileMap, "title");

  // Check if any of the files are uploaded
  const hasFiles = (files: Array<{ title: string; file: string }>): boolean[] => {
    return files.map(({ file: name }) => (value || []).some(({ name: fileName }) => name.toLowerCase() === fileName.toLowerCase()));
  };

  // Returns the file description for the given file & indicates if it has been uploaded
  // If the file is part of a group, it shows the file extension instead of the full name.
  const getFileText = (file: string, uploaded: boolean[], showFullName: boolean, index: number): JSX.Element => {
    const color = uploaded[index] ? "success" : hasError && !uploaded[index] ? "error.main" : "text.secondary";
    const extension = file.split(".").pop();
    return (
      <Typography variant="body3" color={color} component="span" key={file}>
        {showFullName ? file : `.${extension}`}
        {uploaded.length > 1 && uploaded.length - 1 !== index ? ", " : ""}
      </Typography>
    );
  };

  // Returns the row for the given file. If only one file is uploaded, it shows the file name.
  // If multiple files are uploaded, it shows the file extensions.
  const getRow = (key: string): JSX.Element => {
    const files = groupedFiles[key];
    const uploaded = hasFiles(files);
    const success = uploaded.every(Boolean);
    const IconComponent = success ? CheckCircle : CheckCircleOutline;
    const color = success ? "success" : hasError ? "error" : "action";
    const { title } = files[0];
    const isFileNameEqual = files.map(({ file }) => file.split(".")[0]).every((fileName) => fileName === files[0].file.split(".")[0]);
    const fileList = files.map(({ file }, index) => getFileText(file, uploaded, !isFileNameEqual || files.length === 1, index));

    return (
      <TableRow key={title}>
        <TableCell>
          <Box display="flex" alignItems="center">
            <IconComponent color={color} sx={{ mr: 1 }} />
            <Typography sx={{ fontWeight: 500 }} data-testid="file-title">
              {title}
            </Typography>
            <Typography variant="body3" sx={{ ml: 1 }} data-testid="file-extensions">
              ({fileList})
            </Typography>
          </Box>
        </TableCell>
      </TableRow>
    );
  };
  return (
    <TableContainer component={Paper} elevation={0} sx={{ mt: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Required input files</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{Object.keys(groupedFiles).map(getRow)}</TableBody>
      </Table>
    </TableContainer>
  );
};

export default RequiredFiles;
